import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCcw, Lock, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  MessageCircle, Copy, Users, Sparkles, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { api, pollRefresh } from '../lib/api';
import { formatINR } from '../lib/formatINR';
import { GlassCard } from '../components/GlassCard';
import GlassTable from '../components/GlassTable';
import Drawer from '../components/Drawer';

/* severity palette */
const SEV = {
  critical: { bg: 'bg-red-500/15', text: 'text-red-400', rail: 'border-l-red-500' },
  high:     { bg: 'bg-red-500/15', text: 'text-red-400', rail: 'border-l-red-500' },
  warn:     { bg: 'bg-orange-500/15', text: 'text-orange-400', rail: 'border-l-orange-500' },
  medium:   { bg: 'bg-yellow-500/15', text: 'text-yellow-400', rail: 'border-l-yellow-500' },
  low:      { bg: 'bg-emerald-500/15', text: 'text-emerald-400', rail: 'border-l-emerald-500' },
  info:     { bg: 'bg-emerald-500/15', text: 'text-emerald-400', rail: 'border-l-emerald-500' },
};

const CLASS_COLORS = {
  top_performer: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
  on_track: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  at_risk: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  falling_behind: 'bg-red-500/15 text-red-400 border-red-500/25',
};

function relTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.max(0, Math.round(ms / 60000));
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    return `${Math.round(m / 60)}h ago`;
  } catch { return ''; }
}

function maskPhone(ph) {
  if (!ph || ph.length < 4) return ph || '';
  return ph.slice(0, -4).replace(/./g, '*') + ph.slice(-4);
}

export default function SalesStrategy() {
  const { token, hasPlan, planState } = useAuth();
  const isPremium = hasPlan('premium') || planState?.effective_plan === 'trial';
  const toast = useToast();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const genRef = useRef(null);

  /* drawer state */
  const [drawer, setDrawer] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [approved, setApproved] = useState(null);

  /* whatsapp drawer */
  const [waDrawer, setWaDrawer] = useState(null);
  const [waBusy, setWaBusy] = useState(false);
  const [waApproved, setWaApproved] = useState({});

  /* load on mount */
  const load = useCallback(async () => {
    if (!token || !isPremium) return;
    setLoading(true);
    try {
      const r = await api.salesStrategy(token);
      setData(r);
      genRef.current = r?.generated_at || null;
    } catch (e) {
      if (e?.status === 403) return;
      if (e?.status !== 401) toast.show(e?.message || 'Failed to load strategy', 'error');
    } finally { setLoading(false); }
  }, [token, isPremium, toast]);

  useEffect(() => { load(); }, [load]);

  /* refresh */
  const doRefresh = useCallback(async () => {
    if (!token || refreshing) return;
    setRefreshing(true);
    const prev = genRef.current;
    try {
      const r = await pollRefresh({
        postFn: () => api.salesStrategyRefresh(token),
        getFn: () => api.salesStrategy(token),
        prevGeneratedAt: prev,
        pollIntervalMs: 5000,
        maxWaitMs: 130000,
      });
      setData(r);
      genRef.current = r?.generated_at || null;
      toast.show('Sales strategy refreshed', 'success');
    } catch (e) {
      if (e?.status === 429) toast.show('Rate limited — try later', 'error');
      else toast.show(e?.message || 'Refresh failed', 'error');
    } finally { setRefreshing(false); }
  }, [token, refreshing, toast]);

  /* action engine (for take action / markdown) */
  const handleApprove = async () => {
    if (!drawer || actionBusy) return;
    setActionBusy(true);
    try {
      const proposed = await api.proposeAction(token, { type: drawer.type, payload: drawer.payload, source_insight_id: drawer.insightId || undefined });
      await api.approveAction(token, proposed.id);
      setApproved({ id: proposed.id, kind: drawer.type });
      toast.show('Action approved', 'success');
      setDrawer(null);
    } catch (e) { toast.show(e?.message || 'Action failed', 'error'); }
    finally { setActionBusy(false); }
  };

  /* whatsapp reengage */
  const handleReengage = async () => {
    if (!waDrawer || waBusy) return;
    setWaBusy(true);
    try {
      const proposed = await api.proposeAction(token, {
        type: 'payment_reminder',
        payload: { customer_id: waDrawer.customer_id, phone: waDrawer.phone, message: waDrawer.whatsapp_message_draft },
      });
      await api.approveAction(token, proposed.id);
      setWaApproved(prev => ({ ...prev, [waDrawer.customer_id]: true }));
      toast.show('Reminder queued', 'success');
      setWaDrawer(null);
    } catch (e) { toast.show(e?.message || 'Failed', 'error'); }
    finally { setWaBusy(false); }
  };

  /* ── FREE upsell ── */
  if (!isPremium) {
    return (
      <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="strategy-page">
        <div className="relative">
          <GlassCard dark className="p-6 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={20} className="text-purple-400" />
              <h1 className="text-2xl font-extrabold text-white">AI Sales Strategy</h1>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 filter blur-[8px] pointer-events-none select-none" aria-hidden>
              {[1,2,3].map(i => (
                <div key={i} className="glass-card p-5 rounded-2xl">
                  <div className="h-3 w-24 bg-white/10 rounded mb-3" />
                  <div className="h-8 w-32 bg-white/10 rounded" />
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <button data-testid="strategy-upsell-cta" onClick={() => navigate('/billing')}
                className="flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-teal-500 text-white font-extrabold text-sm shadow-2xl hover:shadow-purple-500/30 hover:scale-[1.02] transition-all">
                <Lock size={16} /> Unlock AI Strategy
              </button>
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  /* ── PREMIUM ── */
  const rel = relTime(data?.generated_at);
  const source = data?.source || (data?.cached ? 'cache' : '');
  const degraded = data?.degraded === true;
  const churn = data?.churn || {};
  const salesman = data?.salesman_analytics || {};
  const trends = data?.trends || {};
  const forecast = data?.forecast || {};
  const topActions = data?.top_actions || [];

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto space-y-6" data-testid="strategy-page">
      {/* ── Header ── */}
      <GlassCard dark className="p-4 flex items-center gap-3" testId="strategy-header">
        <Sparkles size={20} className="text-purple-400 flex-shrink-0" />
        <div className="flex-1">
          <h1 className="text-white font-extrabold text-xl">AI Sales Strategy</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {rel && <span className="text-white/40 text-xs" data-testid="strategy-updated">Updated {rel}</span>}
            {source && <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/50 text-[10px] font-bold uppercase tracking-wider" data-testid="strategy-source">{source}</span>}
          </div>
        </div>
        <button onClick={doRefresh} disabled={refreshing || loading} data-testid="strategy-refresh-btn"
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors disabled:opacity-50">
          {refreshing ? <div className="w-3 h-3 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <RefreshCcw size={12} />}
          Refresh
        </button>
      </GlassCard>

      {loading && !data ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <>
          {/* Degraded banner */}
          {degraded && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="strategy-degraded">
              <p className="text-amber-400 font-bold text-sm flex items-center gap-2"><AlertTriangle size={14} /> Using fallback insights — LLM upstream unavailable, retrying</p>
            </div>
          )}

          {/* Headline */}
          {data.headline && (
            <div className="rounded-2xl bg-navy-900 p-5" data-testid="strategy-headline">
              <p className="text-white font-extrabold text-base leading-relaxed">{data.headline}</p>
            </div>
          )}

          {/* Approved banner */}
          {approved && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="strategy-approved">
              <p className="text-emerald-400 font-bold text-sm">Action created</p>
              <p className="text-emerald-400/60 text-xs mt-1">{approved.kind.replace(/_/g, ' ')} · {approved.id?.slice(0, 8)}</p>
            </div>
          )}

          {/* ── Forecast ── */}
          <ForecastCard forecast={forecast} />

          {/* ── Lost Customers (Churn) ── */}
          <ChurnSection churn={churn} waApproved={waApproved} onReengage={setWaDrawer} />

          {/* ── Salesman Performance ── */}
          <SalesmanSection salesman={salesman} />

          {/* ── Trending vs Dying ── */}
          <TrendsSection trends={trends} onMarkdown={(item) => setDrawer({
            title: `Suggest Markdown — ${item.product_name}`,
            type: 'price_change',
            payload: { product_id: item.product_id, product_name: item.product_name, current_price: item.current_price, suggested_discount_pct: 15 },
          })} />

          {/* ── Top Actions ── */}
          {topActions.length > 0 && (
            <div>
              <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Top Actions</h2>
              <div className="space-y-3">
                {topActions.map((a, i) => {
                  const sev = SEV[a.severity] || SEV.info;
                  const showAction = a?.action_engine?.type && a.action_engine.type !== 'none';
                  return (
                    <GlassCard dark key={a.id || i} className={`p-4 border-l-4 ${sev.rail}`} testId={`strategy-action-${a.id || i}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${sev.bg} ${sev.text}`}>{(a.severity || 'info').toUpperCase()}</span>
                        {a.category && <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 text-[10px] font-extrabold uppercase">{a.category}</span>}
                      </div>
                      <p className="text-white font-bold text-sm">{a.title}</p>
                      {a.why && <p className="text-white/60 text-xs mt-1">{a.why}</p>}
                      {a.impact_estimate && <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-white/8 text-white/50 text-xs font-bold">Impact · {a.impact_estimate}</span>}
                      {showAction && (
                        <button
                          data-testid={`strategy-take-action-${a.id || i}`}
                          onClick={() => setDrawer({ title: `Action — ${a.title?.slice(0, 40)}`, type: a.action_engine.type, payload: a.action_engine.payload || {}, insightId: a.id })}
                          className="mt-3 px-4 py-2.5 rounded-full bg-white text-navy-900 text-xs font-bold hover:bg-white/90 transition-colors"
                        >
                          Take Action · {a.action_engine.type.replace(/_/g, ' ')}
                        </button>
                      )}
                    </GlassCard>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* ── Action Drawer ── */}
      <Drawer open={!!drawer} onClose={() => setDrawer(null)} title={drawer?.title || 'Action'}>
        {drawer && (
          <div className="space-y-4">
            <div className="glass-card p-4">
              <p className="text-white/50 text-xs uppercase font-semibold tracking-wider mb-2">Type</p>
              <p className="text-white font-bold text-sm">{drawer.type?.replace(/_/g, ' ')}</p>
            </div>
            {Object.entries(drawer.payload || {}).map(([k, v]) => (
              <div key={k} className="glass-card p-4">
                <p className="text-white/50 text-xs uppercase font-semibold tracking-wider mb-1">{k.replace(/_/g, ' ')}</p>
                <p className="text-white text-sm font-semibold tabular-nums">{typeof v === 'number' ? formatINR(v) : String(v)}</p>
              </div>
            ))}
            <div className="flex gap-3 mt-6">
              <button data-testid="sheet-approve" onClick={handleApprove} disabled={actionBusy}
                className="flex-1 h-12 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold text-sm hover:shadow-lg hover:shadow-teal-500/25 transition-all disabled:opacity-50">
                {actionBusy ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Approve'}
              </button>
              <button data-testid="sheet-dismiss" onClick={() => setDrawer(null)}
                className="flex-1 h-12 rounded-xl bg-white/5 border border-white/10 text-white/60 font-bold text-sm hover:bg-white/10 transition-colors">
                Dismiss
              </button>
            </div>
          </div>
        )}
      </Drawer>

      {/* ── WhatsApp Reengage Drawer ── */}
      <Drawer open={!!waDrawer} onClose={() => setWaDrawer(null)} title={`Reengage — ${waDrawer?.customer_name || ''}`}>
        {waDrawer && (
          <div className="space-y-4">
            <div className="glass-card p-4">
              <p className="text-white/50 text-xs uppercase font-semibold tracking-wider mb-1">Customer</p>
              <p className="text-white font-bold">{waDrawer.customer_name}</p>
              <p className="text-white/50 text-sm mt-0.5">{maskPhone(waDrawer.phone)}</p>
            </div>
            {/* WhatsApp preview */}
            <div className="rounded-2xl bg-[#075E54] p-4" data-testid="whatsapp-preview">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle size={16} className="text-[#25D366]" />
                <p className="text-white/80 text-xs font-bold">WhatsApp Draft</p>
              </div>
              <div className="bg-[#DCF8C6] rounded-xl rounded-tl-sm p-3 max-w-[90%]">
                <p className="text-[#303030] text-sm leading-relaxed whitespace-pre-wrap">{waDrawer.whatsapp_message_draft || 'No draft available'}</p>
              </div>
              <button data-testid="whatsapp-copy-btn"
                onClick={() => { navigator.clipboard.writeText(waDrawer.whatsapp_message_draft || ''); toast.show('Copied to clipboard', 'success'); }}
                className="flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-white/15 text-white text-xs font-bold hover:bg-white/25 transition-colors">
                <Copy size={12} /> Copy
              </button>
            </div>
            <button data-testid="whatsapp-send-btn" onClick={handleReengage} disabled={waBusy}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold text-sm hover:shadow-lg hover:shadow-emerald-500/25 transition-all disabled:opacity-50">
              {waBusy ? <div className="w-5 h-5 border-2 border-white/50 border-t-white rounded-full animate-spin mx-auto" /> : 'Send Reminder'}
            </button>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/* ── Forecast Card ── */
function ForecastCard({ forecast }) {
  if (!forecast?.forecast_next_7d_rupees) return null;
  const daily = forecast.daily_breakdown || [];
  const ci = forecast.confidence_interval || {};
  const maxVal = daily.length > 0 ? Math.max(...daily.map(d => d.forecast_rupees || 0)) : 0;

  return (
    <GlassCard dark className="p-5" testId="strategy-forecast">
      <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mb-1">7-Day Sales Forecast</p>
      <p className="text-white font-extrabold text-3xl tabular-nums">{formatINR(forecast.forecast_next_7d_rupees)}</p>
      {(ci.low != null || ci.high != null) && (
        <p className="text-white/40 text-xs mt-1 tabular-nums">Range {formatINR(ci.low)} – {formatINR(ci.high)}</p>
      )}
      {/* Mini bar chart */}
      {daily.length > 0 && (
        <div className="flex items-end gap-1.5 mt-4 h-16">
          {daily.map((d, i) => {
            const pct = maxVal > 0 ? ((d.forecast_rupees || 0) / maxVal) * 100 : 0;
            const dayLabel = d.date ? new Date(d.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' }) : '';
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-brand-purple/60 rounded-t-md transition-all" style={{ height: `${Math.max(pct, 4)}%` }}
                  title={`${dayLabel}: ${formatINR(d.forecast_rupees)}`} />
                <span className="text-white/30 text-[9px]">{dayLabel}</span>
              </div>
            );
          })}
        </div>
      )}
      {forecast.model && <span className="inline-block mt-3 px-2 py-0.5 rounded bg-white/8 text-white/30 text-[10px] font-bold">{forecast.model}</span>}
    </GlassCard>
  );
}

/* ── Churn / Lost Customers ── */
function ChurnSection({ churn, waApproved, onReengage }) {
  const lost = churn.lost_customers || [];
  return (
    <div data-testid="strategy-churn">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider flex items-center gap-2">
          <Users size={14} className="text-red-400" /> Lost Customers ({churn.count || lost.length})
        </h2>
        {churn.total_value_at_risk_rupees > 0 && (
          <span className="text-red-400 text-xs font-bold tabular-nums">At risk: {formatINR(churn.total_value_at_risk_rupees)}</span>
        )}
      </div>
      {lost.length === 0 ? (
        <GlassCard dark className="p-5 flex items-center gap-4" testId="churn-empty">
          <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
            <Users size={18} className="text-red-400/60" />
          </div>
          <p className="text-white/35 text-sm leading-relaxed">
            No customers meet the lost-customer threshold (≥ 2 purchases prior to the last 60 days). Great retention this window.
          </p>
        </GlassCard>
      ) : (
        <div className="space-y-3">
          {lost.map((c, i) => (
            <GlassCard dark key={c.customer_id || i} className="p-4" testId={`churn-card-${c.customer_id || i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{c.customer_name}</p>
                  <p className="text-white/40 text-xs mt-0.5">{maskPhone(c.phone)}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                    <span>{c.days_since_last_purchase}d since last order</span>
                    <span className="px-1.5 py-0.5 rounded bg-white/8 text-white/60 font-bold tabular-nums">LTV {formatINR(c.lifetime_value_rupees)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {waApproved[c.customer_id] ? (
                    <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-bold border border-emerald-500/25">Reminder sent</span>
                  ) : (
                    <button
                      data-testid={`churn-reengage-btn-${c.customer_id || i}`}
                      onClick={() => onReengage(c)}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white text-xs font-bold hover:shadow-lg hover:shadow-emerald-500/20 transition-all"
                    >
                      Reengage
                    </button>
                  )}
                </div>
              </div>
            </GlassCard>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Salesman Performance ── */
function SalesmanSection({ salesman }) {
  const roster = salesman.roster || [];
  if (roster.length === 0) return null;
  const columns = [
    { key: 'name', label: 'Name', render: v => <span className="text-white font-semibold">{v}</span> },
    { key: 'revenue_rupees', label: 'Revenue', align: 'right', render: v => <span className="text-white/90 tabular-nums">{formatINR(v)}</span> },
    { key: 'orders_count', label: 'Orders', align: 'right', render: v => <span className="tabular-nums">{v}</span> },
    { key: 'avg_ticket_size', label: 'Avg Ticket', align: 'right', render: v => <span className="tabular-nums">{formatINR(v)}</span> },
    { key: 'attainment_pct', label: 'Attainment', align: 'right', render: v => <span className="tabular-nums">{v != null ? `${Math.round(v)}%` : '—'}</span> },
    { key: 'classification', label: 'Status', align: 'center', render: v => {
      const cls = CLASS_COLORS[v] || 'bg-white/5 text-white/40 border-white/10';
      return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border ${cls}`}>{(v || '').replace(/_/g, ' ')}</span>;
    }},
  ];
  return (
    <div data-testid="strategy-salesman">
      <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Salesman Performance</h2>
      <GlassTable columns={columns} data={roster} testId="salesman-table" />
    </div>
  );
}

/* ── Trending vs Dying ── */
function TrendsSection({ trends, onMarkdown }) {
  const trending = trends.trending || [];
  const dying = trends.dying || [];
  if (trending.length === 0 && dying.length === 0) return null;

  return (
    <div data-testid="strategy-trends">
      <h2 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Trending vs Dying Products</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trending Up */}
        <GlassCard dark className="p-4" testId="trends-up">
          <p className="text-emerald-400 font-bold text-sm flex items-center gap-2 mb-3"><TrendingUp size={16} /> Trending Up</p>
          {trending.length === 0 ? <p className="text-white/30 text-xs italic">No trending products</p> : (
            <div className="space-y-2">
              {trending.map((t, i) => (
                <div key={t.product_id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-semibold">{t.product_name}</p>
                    <p className="text-white/40 text-xs">Last 15d: {t.last_15d_units} units</p>
                  </div>
                  <span className="flex items-center gap-1 text-emerald-400 text-sm font-bold tabular-nums">
                    <ArrowUpRight size={14} />{t.velocity_change_pct != null ? `${Math.round(t.velocity_change_pct)}%` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        {/* Dying */}
        <GlassCard dark className="p-4" testId="trends-dying">
          <p className="text-red-400 font-bold text-sm flex items-center gap-2 mb-3"><TrendingDown size={16} /> Dying</p>
          {dying.length === 0 ? <p className="text-white/30 text-xs italic">No dying products</p> : (
            <div className="space-y-2">
              {dying.map((d, i) => (
                <div key={d.product_id || i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-white text-sm font-semibold">{d.product_name}</p>
                    <p className="text-white/40 text-xs">Last 15d: {d.last_15d_units} units</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-red-400 text-sm font-bold tabular-nums">
                      <ArrowDownRight size={14} />{d.velocity_change_pct != null ? `${Math.round(d.velocity_change_pct)}%` : '—'}
                    </span>
                    <button
                      data-testid={`trend-markdown-btn-${d.product_id || i}`}
                      onClick={() => onMarkdown(d)}
                      className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 text-[11px] font-bold hover:bg-white/10 hover:text-white transition-colors"
                    >
                      Suggest Markdown
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
